import React, { useState, useEffect } from "react";
import { authFetch, apiUrl } from "../../utils/api";

/**
 * MakoConnectCard — "use Optimus on the go" via a Telegram bot (Mako).
 *
 * The best of telegram-bridge + hiremako.com, surfaced inside Optimus:
 * one login, connect your BotFather token here, and your agent lives in
 * your Telegram — same brain, same files, same skills as the web. Behind
 * the mako_bridge flag (card hides + endpoints 404 when off).
 *
 * Backend: POST /api/mako/connect, GET /api/mako/status,
 * POST /api/mako/:id/disconnect.
 */
export default function MakoConnectCard() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [botToken, setBotToken] = useState("");
  const [botUsername, setBotUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [freshLinkCode, setFreshLinkCode] = useState("");
  const [freshBotUsername, setFreshBotUsername] = useState("");

  const loadStatus = async () => {
    try {
      const r = await authFetch(apiUrl("/api/mako/status"));
      if (r.status === 404) {
        setEnabled(false);
        return;
      }
      const d = await r.json();
      setBots(d.bots || []);
    } catch (_) {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const connect = async () => {
    setError("");
    if (!botToken.trim()) {
      setError("Paste your bot token from @BotFather first.");
      return;
    }
    setBusy(true);
    try {
      const r = await authFetch(apiUrl("/api/mako/connect"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          botToken: botToken.trim(),
          botUsername: botUsername.trim() || null,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.connected) {
        setError(d.error || "Couldn't connect that bot. Check the token.");
      } else {
        // Capture the username BEFORE clearing so we can build the one-click
        // Telegram deep-link (t.me/<bot>?start=<code>) — the START button then
        // carries the code, so the user never has to type it.
        setFreshBotUsername(botUsername.trim());
        setBotToken("");
        setBotUsername("");
        setShowSetup(false);
        // Surface the one-time link code so the owner can claim the bot.
        if (!d.claimed && d.linkCode) setFreshLinkCode(d.linkCode);
        await loadStatus();
      }
    } catch (_) {
      setError("Network error. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (id) => {
    setBusy(true);
    try {
      await authFetch(apiUrl(`/api/mako/${id}/disconnect`), { method: "POST" });
      await loadStatus();
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return null;

  const connected = bots.length > 0;

  return (
    <div
      className={`hf-panel${connected ? " hf-connected" : ""}`}
      style={{
        marginBottom: "40px",
        padding: "24px",
        borderRadius: "12px",
        border: "1px solid var(--border-primary)",
        background:
          "var(--bg-tertiary)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <img
          src="/images/characters/mako.webp"
          alt="Mako"
          width="44"
          height="44"
          style={{ borderRadius: "12px", flexShrink: 0 }}
        />
        <div>
          <h3 style={{ margin: 0, fontSize: "18px" }}>
            Wanna use Optimus on the go? Mako is ready to roll.
          </h3>
          <p
            style={{
              margin: "4px 0 0",
              color: "var(--text-secondary)",
              fontSize: "14px",
            }}
          >
            Your agent, in your pocket — right inside Telegram. Text or voice
            it, drop photos and PDFs, and it grinds 24/7. Same brain, same
            files, same skills as here on the web — whatever you say to one,
            all of them know.
          </p>
        </div>
      </div>

      {freshLinkCode && (
        <div
          style={{
            marginTop: "16px",
            padding: "16px",
            borderRadius: "12px",
            background: "var(--bg-success-subtle)",
            border: "1px solid var(--border-success)",
          }}
        >
          <p style={{ margin: "0 0 8px", fontSize: "13px", color: "var(--accent-success)", fontWeight: 600 }}>
            One last step — lock your bot to you.
          </p>
          <p style={{ margin: "0 0 10px", fontSize: "13px", color: "var(--text-secondary)" }}>
            Open your bot in Telegram and send this exact message. Until you do,
            the bot won't respond to anyone — that's what keeps strangers out of
            your Optimus OS.
          </p>
          <code
            className="select-all"
            style={{
              display: "block",
              padding: "10px 12px",
              borderRadius: "8px",
              background: "var(--bg-primary)",
              color: "var(--accent-success)",
              fontSize: "14px",
              wordBreak: "break-all",
            }}
          >
            /start {freshLinkCode}
          </code>
          {freshBotUsername && (
            <a
              href={`https://t.me/${freshBotUsername.replace(/^@/, "")}?start=${freshLinkCode}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                marginTop: "12px",
                background: "#229ED9",
                color: "#fff",
                borderRadius: "8px",
                padding: "10px 18px",
                fontWeight: 600,
                fontSize: "14px",
                textDecoration: "none",
              }}
            >
              ✈️ Open Telegram & claim (one tap)
            </a>
          )}
          <p style={{ margin: "10px 0 0", fontSize: "12px", color: "var(--text-secondary)" }}>
            Tip: a brand-new bot shows a <strong>START</strong> button and won't let you
            type first. The button above pre-loads your code, so one tap claims it —
            no typing needed.
          </p>
        </div>
      )}

      {loading ? (
        <p style={{ color: "var(--text-secondary)", fontSize: "13px", marginTop: "16px" }}>
          Checking…
        </p>
      ) : connected ? (
        <div style={{ marginTop: "18px" }}>
          {bots.map((b) => (
            <div
              key={b.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                borderRadius: "10px",
                background: "var(--bg-secondary)",
                border: "1px solid transparent",
                marginBottom: "8px",
              }}
            >
              <span style={{ fontSize: "14px" }}>
                {b.claimed ? "✅ Connected & linked" : "⚠️ Not linked yet"}
                {b.bot_username ? ` — @${b.bot_username}` : ""}
                {!b.claimed && b.link_code ? (
                  <span style={{ display: "block", color: "var(--text-secondary)", fontSize: "12px", marginTop: "2px" }}>
                    Send <code>/start {b.link_code}</code> to your bot to lock it to your account.
                  </span>
                ) : null}
              </span>
              <button
                onClick={() => disconnect(b.id)}
                disabled={busy}
                style={{
                  background: "transparent",
                  border: "1px solid var(--border-primary)",
                  color: "var(--text-secondary)",
                  borderRadius: "8px",
                  padding: "6px 12px",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Disconnect
              </button>
            </div>
          ))}
          <button
            onClick={() => setShowSetup((s) => !s)}
            style={linkBtn}
          >
            {showSetup ? "Cancel" : "Connect another bot"}
          </button>
        </div>
      ) : (
        <button
          onClick={() => setShowSetup((s) => !s)}
          className="hf-press hf-focus"
          style={{
            marginTop: "18px",
            background: "var(--accent-primary)",
            color: "var(--text-on-accent)",
            border: "none",
            borderRadius: "8px",
            padding: "10px 18px",
            fontWeight: 600,
            fontSize: "14px",
            cursor: "pointer",
          }}
        >
          {showSetup ? "Cancel" : "Set up Mako on Telegram"}
        </button>
      )}

      {showSetup && (
        <div
          style={{
            marginTop: "16px",
            padding: "16px",
            borderRadius: "12px",
            background: "var(--bg-secondary)",
            border: "1px solid transparent",
          }}
        >
          {/* 40-second setup walkthrough */}
          <div
            style={{
              position: "relative",
              paddingBottom: "56.25%",
              height: 0,
              borderRadius: "10px",
              overflow: "hidden",
              marginBottom: "16px",
              border: "1px solid transparent",
            }}
          >
            <iframe
              src="https://player.vimeo.com/video/1210880409?title=0&byline=0&portrait=0"
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Mako setup walkthrough"
            />
          </div>
          <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginTop: 0 }}>
            <strong style={{ color: "var(--text-primary)" }}>1.</strong> In Telegram, message{" "}
            <code>@BotFather</code>, send <code>/newbot</code>, pick a name and a
            username ending in "bot", and copy the token it gives you.
            <br />
            <strong style={{ color: "var(--text-primary)" }}>2.</strong> Paste it below and connect.
            Then message your new bot once so it can reach you.
          </p>
          <input
            type="text"
            value={botToken}
            onChange={(e) => setBotToken(e.target.value)}
            placeholder="123456789:AA… (bot token from BotFather)"
            style={input}
          />
          <input
            type="text"
            value={botUsername}
            onChange={(e) => setBotUsername(e.target.value)}
            placeholder="your_bot_username (optional)"
            style={{ ...input, marginTop: "8px" }}
          />
          {error && (
            <p style={{ color: "var(--accent-danger)", fontSize: "13px", margin: "8px 0 0" }}>{error}</p>
          )}
          <button
            onClick={connect}
            disabled={busy}
            className="hf-press hf-focus"
            style={{
              marginTop: "12px",
              background: "var(--accent-primary)",
              color: "var(--text-on-accent)",
              border: "none",
              borderRadius: "8px",
              padding: "10px 18px",
              fontWeight: 600,
              fontSize: "14px",
              cursor: busy ? "default" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? "Connecting…" : "Connect bot"}
          </button>
        </div>
      )}
    </div>
  );
}

const input = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "8px",
  background: "var(--bg-input)",
  border: "1px solid var(--border-secondary)",
  color: "var(--text-primary)",
  fontFamily: "monospace",
  fontSize: "13px",
};
const linkBtn = {
  marginTop: "8px",
  background: "transparent",
  border: "none",
  color: "var(--accent-primary)",
  fontSize: "13px",
  cursor: "pointer",
  padding: 0,
};
